import * as React from 'react';
import { format } from 'date-fns';
import { Calendar as CalendarIcon } from 'lucide-react';
import { Popover, PopoverContent, PopoverTrigger } from './Popover';
import { Button, buttonVariants } from './Button';
import { cn } from './utils';
import { Calendar } from './Calendar';
import { es } from 'date-fns/locale';
import classNames from 'classnames';

export function DateTimePicker({
	disabled,
	className,
	onDateTimeChange,
	title,
	floatingLabel,
	defaultTimestamp,
	containerClassName = '',
	popoverContentClassName = '',
	largeCalendar = false,
	emitInitialValue = true
}: {
	disabled?: boolean;
	className?: string;
	onDateTimeChange: (utcString: string | undefined) => any;
	title?: string;
	floatingLabel?: string;
	defaultTimestamp?: number;
	containerClassName?: string;
	popoverContentClassName?: string;
	largeCalendar?: boolean;
	emitInitialValue?: boolean;
}) {
	const parseDefaultDateTime = React.useCallback(() => {
		if (!defaultTimestamp) return { date: undefined, time: '09:00' };
		const date = new Date(defaultTimestamp * 1000);
		const time = format(date, 'HH:mm');
		return { date, time };
	}, [defaultTimestamp]);

	const { date: initialDate, time: initialTime } = parseDefaultDateTime();
	
	const [date, setDate] = React.useState<Date | undefined>(initialDate);
	const [time, setTime] = React.useState<string>(initialTime);
	const [currentMonth, setCurrentMonth] = React.useState(date?.getMonth() ?? new Date().getMonth());
	const [currentYear, setCurrentYear] = React.useState(date?.getFullYear() ?? new Date().getFullYear());
	const [open, setOpen] = React.useState(false);
	const hasInteracted = React.useRef(emitInitialValue);

	// Combine date and time and return UTC string
	React.useEffect(() => {
		if (disabled || !hasInteracted.current) return;
		if (date && time) {
			const [hours, minutes] = time.split(':').map(Number);
			const combined = new Date(date);
			combined.setHours(hours, minutes, 0, 0);
			onDateTimeChange(combined.toISOString());
		} else {
			onDateTimeChange(undefined);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [date, time]);


	const handleTimeChange = (event: React.ChangeEvent<HTMLInputElement>) => {
		hasInteracted.current = true;
		setTime(event.target.value);
	};

	return (
		<div className={classNames(containerClassName, 'space-y-1')}>
			{title && !floatingLabel && <h3 className="font-medium tracking-tight text-neutral-300">{title}</h3>}
			{floatingLabel && (
				<label className="px-1 text-sm font-medium text-primary-200/80 light:text-primary-900">
					{floatingLabel}
				</label>
			)}
			<div className="flex gap-4">
				{/* Date Selection */}
				<div className="flex-1">
					<div
						data-disabled={disabled || false}
						className={cn('relative grid gap-2 data-[disabled=true]:pointer-events-none data-[disabled=true]:opacity-60')}
					>
						<Popover open={open} onOpenChange={setOpen}>
							<PopoverTrigger asChild>
								<Button
									id="date"
									variant={'default'}
									className={cn(
										'inline-flex h-10 shrink-0 select-none items-center justify-between gap-2 text-nowrap rounded-lg bg-background px-3 text-sm font-medium outline-none transition-colors hover:bg-primary-600 disabled:pointer-events-none disabled:opacity-60 light:bg-neutral-900/70',
										!date && 'font-normal text-neutral-300',
										'border border-neutral-800 bg-neutral-925 transition-all has-[:focus]:border-primary-500 has-[:focus]:outline-none has-[:focus]:ring-2 has-[:focus]:ring-primary-500 light:bg-neutral-950',
										className
									)}
								>
									<div className="flex items-center gap-1 truncate">
										<CalendarIcon className="mr-0.5 h-4 w-4 shrink-0" />
										<span className="truncate">{date ? format(date, 'LLL dd, y', { locale: es }) : 'Seleccionar fecha'}</span>
									</div>
								</Button>
							</PopoverTrigger>
							<PopoverContent
								className={cn(
									'w-auto p-0',
									largeCalendar && 'max-h-[var(--radix-popover-content-available-height)] w-[calc(100vw-32px)] overflow-y-auto sm:w-[360px]',
									popoverContentClassName
								)}
								align="start"
								side="bottom"
								avoidCollisions={!largeCalendar}
								collisionPadding={16}
							>
								<div className="flex flex-col">
									<Calendar
										className={cn(largeCalendar && 'w-full p-3')}
										classNames={
											largeCalendar
												? {
													months: 'w-full',
													month: 'w-full space-y-3',
													table: 'w-full border-collapse',
													head_row: 'grid grid-cols-7',
													head_cell: 'w-full text-center text-sm font-normal text-neutral-400',
													row: 'mt-1 grid grid-cols-7',
													cell: 'relative h-9 w-full p-0 text-center text-sm focus-within:z-20',
													day: cn(buttonVariants({ variant: 'ghost' }), 'h-9 w-full p-0 font-normal aria-selected:opacity-100 hover:bg-neutral-950')
												}
												: undefined
										}
										initialFocus
										mode="single"
										month={new Date(currentYear, currentMonth)}
										selected={date}
										onSelect={(selected) => {
											hasInteracted.current = true;
											setDate(selected);
											if (selected) {
												setCurrentMonth(selected.getMonth());
												setCurrentYear(selected.getFullYear());
												setOpen(false);
											}
										}}
										locale={es}
										numberOfMonths={1}
										onMonthChange={(month) => {
											setCurrentMonth(month.getMonth());
											setCurrentYear(month.getFullYear());
										}}
									/>
									
								</div>
							</PopoverContent>
						</Popover>
					</div>
				</div>

				{/* Time Selection */}
				<div className="flex-1">
					<div className="relative">
						<input
							type="time"
							value={time}
							onChange={handleTimeChange}
							className={cn(
								'inline-flex h-10 w-full select-none items-center rounded-lg border border-neutral-800 bg-neutral-925 px-3 text-sm font-medium text-neutral-200 outline-none transition-all placeholder:text-neutral-400 focus:border-primary-500 focus:ring-2 focus:ring-primary-500 disabled:pointer-events-none disabled:opacity-60 light:bg-neutral-950 appearance-none [&::-webkit-calendar-picker-indicator]:hidden [&::-webkit-calendar-picker-indicator]:appearance-none',
								!time && 'text-neutral-400'
							)}
							disabled={disabled}
						/>
					</div>
				</div>
			</div>
		</div>
	);
} 